import React from 'react';
import {
  Box,
  Chip,
  Collapse,
  Grid,
  IconButton,
  Paper,
  Slider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';

// --------------------------------------------------------------------------
// Formatters (self-contained)
// --------------------------------------------------------------------------
export const usd0 = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `$${Math.round(Number(v)).toLocaleString()}`;
export const num = (v) =>
  v == null || Number.isNaN(Number(v))
    ? '—'
    : Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
export const pct = (v) =>
  v == null || Number.isNaN(Number(v)) ? '—' : `${(Number(v) * 100).toFixed(1)}%`;

const DIRECTION_TONE = {
  increase: 'success',
  pull_back: 'warning',
  saturated: 'error',
  hold: 'default',
};
const DIRECTION_LABEL = {
  increase: 'Scale up',
  pull_back: 'Pull back',
  saturated: 'At capacity',
  hold: 'On track',
};

// --------------------------------------------------------------------------
// ComGauge — cost of marketing vs goal, colored by efficiency band
// --------------------------------------------------------------------------
export function ComGauge({ com, goal, size = 'small' }) {
  if (com == null) {
    return <Chip size={size} variant="outlined" label="CoM —" />;
  }
  let color = 'default';
  if (goal) {
    const ratio = com / goal;
    color = ratio <= 0.85 ? 'success' : ratio <= 1.1 ? 'warning' : 'error';
  }
  return <Chip size={size} color={color} label={`${pct(com)}${goal ? ` / ${pct(goal)}` : ''}`} />;
}

// --------------------------------------------------------------------------
// ScenarioSummary — compact client-total cards (get-started projections style).
// Each card shows the expected value big + the low–high range as subtext.
// --------------------------------------------------------------------------
export function ScenarioSummary({ scenarios, goalCom }) {
  if (!scenarios) return null;
  const exp = scenarios.expected || {};
  const lo = scenarios.low || {};
  const hi = scenarios.high || {};
  const cards = [
    { label: 'Net revenue', val: usd0(exp.net_revenue), sub: `${usd0(lo.net_revenue)} – ${usd0(hi.net_revenue)}`, big: true },
    { label: 'Leads', val: num(exp.leads), sub: `${num(lo.leads)} – ${num(hi.leads)}` },
    { label: 'Appointments', val: num(exp.appointments), sub: `${num(lo.appointments)} – ${num(hi.appointments)}` },
    { label: 'Booked', val: num(exp.booked), sub: `${num(lo.booked)} – ${num(hi.booked)}` },
    { label: 'Cost of marketing', val: pct(exp.com), sub: goalCom ? `goal ${pct(goalCom)}` : `${pct(hi.com)} – ${pct(lo.com)}` },
    { label: 'Spend', val: usd0(exp.spend), sub: '' },
  ];
  return (
    <Grid container spacing={1.5}>
      {cards.map((c) => (
        <Grid item xs={6} sm={4} md={2} key={c.label}>
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50', height: '100%' }}>
            <Typography color="text.secondary" variant="caption">{c.label}</Typography>
            <Typography variant={c.big ? 'h5' : 'h6'} sx={{ fontWeight: 700, lineHeight: 1.25 }}>
              {c.val}
            </Typography>
            {c.sub ? (
              <Typography color="text.secondary" variant="caption">{c.sub}</Typography>
            ) : null}
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
}

// --------------------------------------------------------------------------
// DiminishingReturnsChart — projected leads across the spend range, with the
// current operating point marked (concave when the elasticity fit found k>0).
// --------------------------------------------------------------------------
export function DiminishingReturnsChart({ segment, daysInMonth, appliedBudget, height = 160 }) {
  const curve = segment?.curve;
  if (!curve || !curve.cpl_at_base) return null;

  const floor = segment.spend_floor ?? segment.base_budget * 0.5;
  const ceiling = segment.spend_ceiling ?? segment.base_budget * 2;
  const span = Math.max(ceiling - floor, 1);
  const N = 24;
  const data = [];
  for (let i = 0; i <= N; i += 1) {
    const spend = floor + (span * i) / N;
    const daily = daysInMonth ? spend / daysInMonth : spend / 30;
    let cpl = curve.cpl_at_base;
    if (curve.k && curve.spend_base_daily && daily > 0) {
      cpl = curve.cpl_at_base * (1 + curve.k * Math.log(daily / curve.spend_base_daily));
    }
    const leads = cpl > 0 ? spend / cpl : 0;
    data.push({ spend: Math.round(spend), leads: Math.round(leads) });
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey="spend" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
        <YAxis tick={{ fontSize: 10 }} width={28} />
        <RechartsTooltip
          formatter={(value, name) => (name === 'leads' ? [num(value), 'Leads'] : [usd0(value), name])}
          labelFormatter={(v) => `Spend ${usd0(v)}`}
        />
        <Line type="monotone" dataKey="leads" stroke="#1976d2" strokeWidth={2} dot={false} isAnimationActive={false} />
        {appliedBudget != null && (
          <ReferenceLine x={Math.round(appliedBudget)} stroke="#9c27b0" strokeDasharray="4 3" />
        )}
      </LineChart>
    </ResponsiveContainer>
  );
}

// --------------------------------------------------------------------------
// SegmentTable — scannable one-row-per-segment view, sorted exceptions-first.
// Expand a row to reveal its recommendation detail, budget slider, and curve.
// Stays digestible for clients with many segments.
// --------------------------------------------------------------------------
function SegmentDetail({ segment, rec, value, goalCom, daysInMonth, onSlider }) {
  const direction = rec?.direction || 'hold';
  const tone = DIRECTION_TONE[direction] || 'default';
  const floor = Math.max(0, Math.round(segment.spend_floor ?? segment.base_budget * 0.5));
  const ceiling = Math.round(segment.spend_ceiling ?? segment.base_budget * 2);
  const step = Math.max(50, Math.round((ceiling - floor) / 100));

  return (
    <Box sx={{ px: 3, py: 2, bgcolor: 'action.hover' }}>
      {rec && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{rec.headline}</Typography>
          {direction !== 'hold' && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Chip size="small" variant="outlined" label={`Now ${usd0(rec.current_budget)}/mo`} />
              <Typography color="text.secondary">→</Typography>
              <Chip size="small" color={tone} label={`Suggest ${usd0(rec.recommended_budget)}/mo`} />
            </Stack>
          )}
          <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
            {(rec.why || []).map((w, i) => (
              <Typography key={i} component="li" variant="caption" color="text.secondary">{w}</Typography>
            ))}
          </Box>
        </Box>
      )}
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <Typography variant="caption" color="text.secondary">
            Model a different budget for this segment
          </Typography>
          <Slider
            value={Math.min(Math.max(value, floor), ceiling)}
            min={floor}
            max={ceiling}
            step={step}
            onChange={(_e, v) => onSlider(segment.budget_id, Array.isArray(v) ? v[0] : v)}
            valueLabelDisplay="auto"
            valueLabelFormat={usd0}
          />
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">{usd0(floor)}</Typography>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>{usd0(value)}/mo</Typography>
            <Typography variant="caption" color="text.secondary">{usd0(ceiling)}</Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <DiminishingReturnsChart segment={segment} daysInMonth={daysInMonth} appliedBudget={value} />
        </Grid>
      </Grid>
    </Box>
  );
}

export function SegmentTable({
  segments, recById, outputsById, budgets, goalCom, daysInMonth, expanded, onToggle, onSlider,
}) {
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontWeight: 700, whiteSpace: 'nowrap' } }}>
            <TableCell sx={{ width: 36 }} />
            <TableCell>Segment</TableCell>
            <TableCell>Recommendation</TableCell>
            <TableCell align="right">Budget</TableCell>
            <TableCell align="right">Net revenue (next month)</TableCell>
            <TableCell align="right">CoM</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {segments.map((seg) => {
            const id = seg.budget_id;
            const rec = recById[id];
            const out = outputsById[id] || seg.outputs || {};
            const exp = out.expected || {};
            const lo = out.low || {};
            const hi = out.high || {};
            const direction = rec?.direction || 'hold';
            const tone = DIRECTION_TONE[direction] || 'default';
            const isOpen = expanded.has(id);
            const value = budgets[id] ?? seg.base_budget;
            return (
              <React.Fragment key={id}>
                <TableRow
                  hover
                  sx={{ cursor: 'pointer', '& > td': { borderBottom: isOpen ? 'none' : undefined } }}
                  onClick={() => onToggle(id)}
                >
                  <TableCell sx={{ pr: 0 }}>
                    <IconButton size="small">
                      {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </IconButton>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{seg.scope_label || 'Segment'}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
                      {seg.scope_mode}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" color={tone} label={DIRECTION_LABEL[direction] || direction} />
                  </TableCell>
                  <TableCell align="right">{usd0(value)}/mo</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700}>{usd0(exp.net_revenue)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {usd0(lo.net_revenue)} – {usd0(hi.net_revenue)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <ComGauge com={exp.com} goal={goalCom} />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell colSpan={6} sx={{ p: 0, border: isOpen ? undefined : 'none' }}>
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <SegmentDetail
                        segment={seg}
                        rec={rec}
                        value={value}
                        goalCom={goalCom}
                        daysInMonth={daysInMonth}
                        onSlider={onSlider}
                      />
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
